import { useContext, useEffect, useState } from "react";
import { Backdrop, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import AppRoutes from "./routes/app-routes";
import { useKeycloak } from "../auth";
import "./config/i18n";
import ErrorComponent from "./components/error/error";
import MainLayout from "./layout/main-layout";
import SuccessComponent from "./components/success/SuccessComponent";
import pubSub, { EVENTS } from "./config/pubSub";
import { clearStorage, setTokenToStorage } from "./config/storageConfig";
import { AppConfig } from "./context/context";
import PageNotFound from "./pages/404/404";
import UserService from "./services/user-service";
import { BRAND_ICONS } from "./utils/constants";
import useTimeout from "./utils/useTimeout";
import { parseLanguage } from "./utils/utils";
import { useIdleTimerContext } from 'react-idle-timer';

export const LICENSE_MODE_APP = "KCIM";

export function App() {
  const [open, setOpen] = useState<boolean>(false);
  const [showLongLoadingMessage, setShowLongLoadingMessage] = useState<boolean>(false);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const { keycloak } = useKeycloak();
  const { config, userProfile } = useContext(AppConfig) || {};
  const { setConfig, setUserProfile } = useContext(AppConfig)!;

  if (!config || !userProfile) {
    throw new Error("AppConfig context is not properly initialized.");
  }

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [shouldShow404, setShouldShow404] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  //const searchParams = new URLSearchParams(window.location.search);
  const idleTimer = useIdleTimerContext();

  // oauth code 
  /*useEffect(()=> {
    if(searchParams.has("code")) {
      window.addEventListener("message", function (event: any) {
        if (event.data.message === "requestResult") {
          event.source.postMessage({"message": "deliverResult", result: {code: searchParams.get("code")} }, "*");
        }
      });
    }
  }, [searchParams])*/

  const handleOnIdle = () => {
    clearStorage();
    keycloak.logout();
  };

  useEffect(()=> {
    idleTimer.activate()
  },[idleTimer, idleTimer.activate()])
    
  const initUserProfile = async () => {
    const userInfo: any = await keycloak.loadUserInfo();
    const service: any = UserService({
      baseUrl: config.baseUrl,
      tenantId: config.tenantId,
      token: keycloak.token,
    });
    
    // Defensive check: Ensure service exists before calling getUserSetting
    const userSetting: any = service?.getUserSetting ? await service.getUserSetting(userInfo?.sub) : {};

    const userProfile = {
      email: userInfo?.email,
      emailVerified: userInfo?.email_verified,
      lastname: userInfo?.family_name,
      firstname: userInfo?.given_name,
      username: userInfo?.preferred_username,
      id: userInfo?.sub,
      locale: userSetting?.locale || userInfo?.locale,
      role: userSetting?.role || "user",
    };
    return userProfile;
  };

  useEffect(() => {
    pubSub.subscribe(EVENTS.PROGRESS, (status: boolean) => setOpen(status));    
  }, []);

  useEffect(() => {
    let timer: any;
    if (open) {
      timer = setTimeout(() => {
        setShowLongLoadingMessage(true);
      }, 2000);
    } else {
      setShowLongLoadingMessage(false);
    }
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const init = async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: "login-required",
          pkceMethod: "S256",
          enableLogging: true,
          flow: "standard",
          // scope: 'offline_access',
        });
        setAuthenticated(authenticated);
        setIsInitialized(true);
        if (authenticated) {
          setTokenToStorage(false, keycloak.token);
          const userProfile: any = await initUserProfile();
          setUserProfile(userProfile);
          setConfig((prevConfig: any) => {
            return {
              ...prevConfig,
              language: userProfile.locale ?? parseLanguage(i18n.language),
              token: keycloak.token,
            };
          });
        }
      } catch (error) {
        console.error("Failed to initialize:", error);
      }
    };
    init();
  }, [keycloak]);

  useEffect(() => {
    if (config.language !== i18n.language) {
      i18n.changeLanguage(config.language);
    }
  }, [i18n, config.language]);

  useEffect(() => {
    const windowName = "dev_portal"
    const faviconUpdate = async () => {
      const favicon = document.getElementById("favicon");
      favicon?.setAttribute(
        "href",
        `${process.env.PUBLIC_URL}/${BRAND_ICONS["Kyocera"]}`
      );
    };
    faviconUpdate();
    window.name = windowName
  }, [config.configBrand]);

  useEffect(() => {
    window.process = {
      ...window.process,
    };
  }, []);
  
  useTimeout(() => {
    if (!isInitialized) setShouldShow404(true);
  }, 10000);

  keycloak.onTokenExpired = async () => {
    try {
      await keycloak.updateToken(60);
      setTokenToStorage(false, keycloak.token);
    } catch (error) {
      console.error("Failed to refresh:", error);
      handleOnIdle();
    }
  };

  if (!authenticated) {
    return shouldShow404 ? (
      <MainLayout>
        <PageNotFound />
      </MainLayout>
    ) : (
      <Backdrop sx={{ zIndex: 9999, color: "var(--kyocera-white)", flexDirection: "column" }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <>
      <Backdrop sx={{ zIndex: 9999, color: "var(--kyocera-white)", flexDirection: "column" }} open={open}>
        <CircularProgress color="inherit" />
        {showLongLoadingMessage && (
          <>
            <Typography variant="h5" sx={{ mt: 4, fontWeight: "bold" }}>{t("processing")}</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>{t("pleasewait")}</Typography>
          </>
        )}
      </Backdrop>
      <ErrorComponent/>
      <SuccessComponent />
      <RouterProvider router={AppRoutes} />
    </>
  );
}

export default App;
